// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Client-Side Calculation Engine
// Mirrors the server engine for instant "live preview" without API round-trips.
// The server is the source of truth for saved scenarios.
// ─────────────────────────────────────────────────────────────────────────────
import type { LCCAParameters, LCCAResults, CashFlowYear, SensitivityResult } from '@lcca-nexus/shared';

export function calculateLCCA(params: LCCAParameters): LCCAResults {
  const { analysisPeriod: N, discountRate, initialCost, residualValue,
          annualOM, omEscalation, annualEnergy, energyEscalation, replacements } = params;

  const d    = discountRate / 100;
  const e_om = omEscalation / 100;
  const e_en = energyEscalation / 100;

  const replMap = new Map<number, number>();
  for (const r of replacements) {
    if (r.year >= 1 && r.year <= N) {
      replMap.set(r.year, (replMap.get(r.year) ?? 0) + r.cost);
    }
  }

  const cashFlows: CashFlowYear[] = [];
  let cumulativePW = initialCost;
  let omUndiscounted = 0, energyUndiscounted = 0, replacementUndiscounted = 0;
  let omPW = 0, energyPW = 0, replacementPW = 0;

  for (let t = 1; t <= N; t++) {
    const df              = Math.pow(1 + d, -t);
    const omCost          = annualOM * Math.pow(1 + e_om, t - 1);
    const energyCost      = annualEnergy * Math.pow(1 + e_en, t - 1);
    const replacementCost = replMap.get(t) ?? 0;
    const totalCost       = omCost + energyCost + replacementCost;
    const presentWorth    = totalCost * df;
    cumulativePW         += presentWorth;

    omUndiscounted          += omCost;
    energyUndiscounted      += energyCost;
    replacementUndiscounted += replacementCost;
    omPW                    += omCost * df;
    energyPW                += energyCost * df;
    replacementPW           += replacementCost * df;

    cashFlows.push({ year: t, omCost, energyCost, replacementCost, totalCost, discountFactor: df, presentWorth, cumulativePW });
  }

  const residualPW = residualValue * Math.pow(1 + d, -N);
  const totalLCC   = initialCost + omPW + energyPW + replacementPW - residualPW;
  const uac        = d === 0
    ? totalLCC / N
    : totalLCC * (d / (1 - Math.pow(1 + d, -N)));

  const sensitivity = computeSensitivity(params, totalLCC);

  return {
    totalLCC, uac, initialCostPW: initialCost,
    omPW, energyPW, replacementPW, residualPW,
    omUndiscounted, energyUndiscounted, replacementUndiscounted,
    cashFlows, sensitivity,
    calculatedAt: new Date().toISOString(),
  };
}

function computeSensitivity(params: LCCAParameters, baseLCC: number): SensitivityResult[] {
  const tests: Array<{ param: keyof LCCAParameters; label: string; delta: number }> = [
    { param: 'discountRate',   label: 'Discount Rate',    delta:  0.1 },
    { param: 'discountRate',   label: 'Discount Rate',    delta: -0.1 },
    { param: 'initialCost',    label: 'Initial Cost',     delta:  0.1 },
    { param: 'annualOM',       label: 'Annual O&M',       delta:  0.1 },
    { param: 'annualEnergy',   label: 'Annual Energy',    delta:  0.1 },
    { param: 'analysisPeriod', label: 'Analysis Period',  delta:  0.1 },
    { param: 'residualValue',  label: 'Residual Value',   delta:  0.1 },
  ];

  return tests.map(({ param, label, delta }) => {
    const p2 = { ...params } as Record<string, number | unknown[]>;
    const val = p2[param] as number;
    p2[param] = param === 'analysisPeriod' ? Math.round(val * (1 + delta)) : val * (1 + delta);
    const res = calculateLCCA(p2 as unknown as LCCAParameters);
    const deltaLCC = res.totalLCC - baseLCC;
    return {
      parameter: param as SensitivityResult['parameter'],
      label,
      perturbation: delta > 0 ? '+10%' as const : '-10%' as const,
      newLCC: res.totalLCC,
      deltaLCC,
      deltaPercent: (deltaLCC / baseLCC) * 100,
    };
  });
}
